// resignationAPI.ts
import axios from "axios";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";

const baseURL = `${API_BASE_PATH}/resignations`;

const apiClient = axios.create({
  baseURL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/x-www-form-urlencoded",
  },
});

// 🔹 Helper to convert object to x-www-form-urlencoded
const toFormUrlEncoded = (data: Record<string, any>) =>
  new URLSearchParams(data).toString();

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

export const resignationApi = {
  // GET all resignations
  getAllResignations: async (token: string | null) => {
    if (!token) {
      const msg = "Authentication token is missing";
      toast.error(msg);
      throw new Error(msg);
    }

    try {
      const response = await apiClient.get("/form", {
        
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      const msg = extractErrorMessage(error);
      toast.error(msg);
      throw new Error(msg);
    }
  },

  // GET employee and department data for dropdown
  getEmployeeDepartmentDropdown: async (token: string | null) => {
    if (!token) {
      const msg = "Authentication token is missing";
      toast.error(msg);
      throw new Error(msg);
    }

    try {
      const response = await apiClient.get("/get_using_empId_department", {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data; 
    } catch (error) {
      const msg = extractErrorMessage(error);
      toast.error(msg);
      throw new Error(msg);
    }
  },

  // POST - Submit resignation
  submitResignation: async (
    token: string | null,
    data: {
      employee_id: string;
      reason: string;
      notice_date: string;
      resignation_date: string;
    }
  ) => {
    if (!token) {
      const msg = "Authentication token is missing";
      toast.error(msg);
      throw new Error(msg);
    }

    try {
      const response = await apiClient.post("/", toFormUrlEncoded(data), {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Resignation submitted successfully");
      return response.data;
    } catch (error) {
      const msg = extractErrorMessage(error);
      toast.error(msg);
      throw new Error(msg);
    }
  },

  // PUT - Update resignation
 updateResignation: async (
  token: string | null,
  employee_id: string,
  data: {
    reason?: string;
    notice_date?: string | null;
    resignation_date?: string | null;
    status?: string;   // 👈 added
  }
) => {
  if (!token) throw new Error("Authentication token is missing");

  try {
    const response = await apiClient.put(
      `/update/${employee_id}`,
      toFormUrlEncoded(data),
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    toast.success("Resignation updated successfully");
    return response.data;
  } catch (error) {
    console.error(`Error updating resignation for ${employee_id}:`, error);
    throw error;
  }
},


  // DELETE - Remove resignation
  deleteResignation: async (token: string | null, employee_id: string) => {
    if (!token) {
      const msg = "Authentication token is missing";
      toast.error(msg);
      throw new Error(msg);
    }

    try {
      const response = await apiClient.delete(`/${employee_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 204) {
        toast.success("Resignation deleted successfully");
        return true;
      }
      return false;
    } catch (error) {
      const msg = extractErrorMessage(error);
      toast.error(msg);
      throw new Error(msg);
    }
  },
};
